<template>
  <div class="directions-panel" :class="{ 'is-expanded': isExpanded }">
    <!-- Collapsed state - just a button -->
    <div v-if="!isExpanded" class="directions-toggle" @click="togglePanel">
      <WFIcon name="path" class="w-24 h-24"></WFIcon>
      <span class="toggle-label">Juhised</span>
    </div>

    <!-- Expanded state - full panel -->
    <div v-else class="directions-content">
      <div class="directions-header">
        <span class="header-title">Juhised</span>
        <button class="close-btn" @click="togglePanel">×</button>
      </div>

      <div class="directions-body">
        <!-- Start Location -->
        <div class="location-input">
          <div class="input-icon start-icon">
            <div class="icon-dot start-dot"></div>
          </div>
          <div class="input-wrapper">
            <input
              type="text"
              v-model="startSearch"
              @input="searchStart"
              @focus="onInputFocus('start')"
              @click="onInputFocus('start')"
              :placeholder="startPlaceholder"
              class="location-field"
              :class="{ 'input-active': activeInput === 'start' }"
              :readonly="showKeyboard"
            />
            <button v-if="startPOI" class="clear-btn" @click="clearStart">×</button>
          </div>
        </div>

        <!-- End Location -->
        <div class="location-input">
          <div class="input-icon end-icon">
            <div class="icon-dot end-dot"></div>
          </div>
          <div class="input-wrapper">
            <input
              type="text"
              v-model="endSearch"
              @input="searchEnd"
              @focus="onInputFocus('end')"
              @click="onInputFocus('end')"
              :placeholder="endPlaceholder"
              class="location-field"
              :class="{ 'input-active': activeInput === 'end' }"
              :readonly="showKeyboard"
            />
            <button v-if="endPOI" class="clear-btn" @click="clearEnd">×</button>
          </div>
        </div>

        <!-- Swap button -->
        <button class="swap-btn" @click="swapLocations" title="Vaheta asukohti">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"/>
          </svg>
        </button>

        <!-- Search Results Dropdown -->
        <div v-if="searchResults.length > 0" class="search-results">
          <div
            v-for="poi in searchResults"
            :key="poi.id || poi.getId()"
            class="search-result-item"
            @click="selectPOI(poi)"
          >
            <img v-if="getPOIIcon(poi)" :src="getPOIIcon(poi)" class="result-icon" />
            <div class="result-info">
              <span class="result-name">{{ getPOIName(poi) }}</span>
              <span class="result-floor" v-if="getFloorName(poi)">{{ getFloorName(poi) }}</span>
            </div>
          </div>
        </div>

        <!-- Options -->
        <div class="directions-options">
          <label class="option-checkbox">
            <input type="checkbox" v-model="avoidStairs" />
            <img src="@/assets/Wheelchair.svg" alt="Accessible route" class="wheelchair-icon" />
            <span>Väldi treppe ja eskalaatoreid</span>
          </label>
        </div>
      </div>

      <div class="directions-footer">
        <button 
          class="show-route-btn th-btn th-btn-active"
          :disabled="!canShowRoute"
          @click="showRoute"
        >
          <WFIcon name="path" class="w-20 h-20"></WFIcon>
          <span>Näita teed</span>
        </button>
        <button 
          v-if="hasActiveRoute"
          class="clear-route-btn th-btn th-btn-stroke"
          @click="clearRoute"
        >
          Tühista
        </button>
      </div>
      
      <!-- Built-in Keyboard -->
      <div v-if="showKeyboard && activeInput" class="directions-keyboard">
        <div class="keyboard-row">
          <div v-for="key in keyboardLayout[0]" :key="key" class="keyboard-key" @click="onKeyPress(key)">{{ key }}</div>
        </div>
        <div class="keyboard-row">
          <div v-for="key in keyboardLayout[1]" :key="key" class="keyboard-key" @click="onKeyPress(key)">{{ key }}</div>
        </div>
        <div class="keyboard-row">
          <div v-for="key in keyboardLayout[2]" :key="key" class="keyboard-key" @click="onKeyPress(key)">{{ key }}</div>
        </div>
        <div class="keyboard-row">
          <div v-for="key in keyboardLayout[3]" :key="key" class="keyboard-key" :class="{ 'key-delete': key === 'DEL', 'key-space': key === ' ' }" @click="onKeyPress(key)">
            {{ key === ' ' ? '' : key }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "DirectionsPanel",
  
  computed: {
    ...mapState("wf", ["pois", "language", "floors", "portrait", "mobile"]),
    
    // Show keyboard only on kiosk (not mobile)
    showKeyboard() {
      return !this.mobile;
    },
    
    canShowRoute() {
      return this.startPOI && this.endPOI;
    },
    
    // Check if we're on mobile/portrait where we should auto-collapse
    shouldAutoCollapse() {
      // Use Vuex portrait state if available, otherwise check window
      if (this.portrait !== undefined) {
        return this.portrait;
      }
      // Fallback: check window orientation/size
      return window.innerWidth <= 1080 || window.matchMedia('(orientation: portrait)').matches;
    },
    
    startPlaceholder() {
      return this.language === 'et' ? 'Vali alguspunkt...' : 
             this.language === 'en' ? 'Select start point...' : 
             this.language === 'ru' ? 'Выберите начало...' : 'Vali alguspunkt...';
    },
    
    endPlaceholder() {
      return this.language === 'et' ? 'Vali sihtkoht...' : 
             this.language === 'en' ? 'Select destination...' : 
             this.language === 'ru' ? 'Выберите пункт назначения...' : 'Vali sihtkoht...';
    },
    
    // Get all available POIs for searching
    availablePOIs() {
      // Try multiple ways to get POIs
      if (this.pois && this.pois.length > 0) {
        return this.pois;
      }
      // Try from wayfinder directly
      if (this.$wayfinder && this.$wayfinder.pois) {
        const poisArray = [];
        this.$wayfinder.pois.forEach(poi => poisArray.push(poi));
        return poisArray;
      }
      // Try getPOIs method
      if (this.$wayfinder && this.$wayfinder.getPOIs) {
        return this.$wayfinder.getPOIs();
      }
      return [];
    }
  },
  
  data() {
    return {
      isExpanded: false,
      startSearch: '',
      endSearch: '',
      startPOI: null,
      endPOI: null,
      searchResults: [],
      activeInput: null,
      avoidStairs: false,
      hasActiveRoute: false,
      // Estonian keyboard layout
      keyboardLayout: [
        ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
        ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', 'Ü', 'Õ'],
        ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ö', 'Ä'],
        ['Z', 'X', 'C', 'V', 'B', 'N', 'M', 'DEL'],
        [' ']
      ]
    };
  },
  
  mounted() {
    // Debug: log available POIs
    console.log('DirectionsPanel mounted');
    console.log('POIs from store:', this.pois);
    console.log('POIs count:', this.availablePOIs.length);
  },
  
  watch: {
    // Close search results when clicking outside
    activeInput(val) {
      if (!val) {
        this.searchResults = [];
      }
    },
    pois(newPois) {
      console.log('POIs updated:', newPois?.length);
    }
  },
  
  methods: {
    togglePanel() {
      this.isExpanded = !this.isExpanded;
      if (!this.isExpanded && !this.hasActiveRoute) {
        // Only reset if there's no active route
        this.resetPanel();
      } else if (this.isExpanded) {
        // Debug when panel opens
        console.log('Panel opened, available POIs:', this.availablePOIs.length);
      }
    },
    
    // Input focus handling
    onInputFocus(inputType) {
      this.activeInput = inputType;
      // Clear previous POI selection when re-focusing
      if (inputType === 'start' && this.startPOI) {
        // Keep the search text but allow editing
      } else if (inputType === 'end' && this.endPOI) {
        // Keep the search text but allow editing
      }
      // Trigger search with current text
      if (inputType === 'start') {
        this.searchPOIs(this.startSearch);
      } else {
        this.searchPOIs(this.endSearch);
      }
    },
    
    // Keyboard key press handler
    onKeyPress(key) {
      if (!this.activeInput) return;
      
      if (key === 'DEL') {
        // Delete last character
        if (this.activeInput === 'start') {
          this.startSearch = this.startSearch.slice(0, -1);
          this.startPOI = null; // Clear POI when editing
          this.searchPOIs(this.startSearch);
        } else {
          this.endSearch = this.endSearch.slice(0, -1);
          this.endPOI = null;
          this.searchPOIs(this.endSearch);
        }
      } else {
        // Add character
        if (this.activeInput === 'start') {
          this.startSearch += key;
          this.startPOI = null;
          this.searchPOIs(this.startSearch);
        } else {
          this.endSearch += key;
          this.endPOI = null;
          this.searchPOIs(this.endSearch);
        }
      }
    },
    
    searchStart() {
      this.activeInput = 'start';
      this.searchPOIs(this.startSearch);
    },
    
    searchEnd() {
      this.activeInput = 'end';
      this.searchPOIs(this.endSearch);
    },
    
    searchPOIs(query) {
      console.log('Searching for:', query);
      
      if (!query || query.length < 1) {
        this.searchResults = [];
        return;
      }
      
      const poisToSearch = this.availablePOIs;
      
      if (!poisToSearch || poisToSearch.length === 0) {
        console.warn('No POIs available for search');
        return;
      }
      
      // Normalize search query - remove special characters, convert to lowercase
      const normalizeText = (text) => {
        return text
          .toLowerCase()
          .replace(/&/g, '')      // Remove &
          .replace(/[^\w\s]/g, '') // Remove other special chars
          .replace(/\s+/g, '');    // Remove spaces
      };
      
      const normalizedQuery = normalizeText(query);
      const lowerQuery = query.toLowerCase();
      const lang = this.language || 'et';
      
      this.searchResults = poisToSearch
        .filter(poi => {
          try {
            // Get name with language parameter
            let name = '';
            
            if (typeof poi.getName === 'function') {
              name = poi.getName(lang);
            }
            
            // Fallback to names.translations
            if (!name && poi.names && poi.names.translations) {
              const trans = poi.names.translations;
              name = trans[lang] || trans.et || trans.en || '';
            }
            
            if (!name || name === false) {
              return false;
            }
            
            const nameStr = String(name);
            const lowerName = nameStr.toLowerCase();
            const normalizedName = normalizeText(nameStr);
            
            // Match either exact or normalized (e.g., "hm" matches "H&M")
            return lowerName.includes(lowerQuery) || normalizedName.includes(normalizedQuery);
          } catch (e) {
            return false;
          }
        })
        .slice(0, 8);
      
      console.log('Search results:', this.searchResults.length);
    },
    
    selectPOI(poi) {
      const name = this.getPOIName(poi);
      console.log('Selected POI:', name);
      
      if (this.activeInput === 'start') {
        this.startPOI = poi;
        this.startSearch = name;
      } else if (this.activeInput === 'end') {
        this.endPOI = poi;
        this.endSearch = name;
      }
      this.searchResults = [];
      this.activeInput = null;
    },
    
    clearStart() {
      this.startPOI = null;
      this.startSearch = '';
    },
    
    clearEnd() {
      this.endPOI = null;
      this.endSearch = '';
    },
    
    swapLocations() {
      const tempPOI = this.startPOI;
      const tempSearch = this.startSearch;
      
      this.startPOI = this.endPOI;
      this.startSearch = this.endSearch;
      
      this.endPOI = tempPOI;
      this.endSearch = tempSearch;
    },
    
    getFloorName(poi) {
      try {
        const node = poi.getNode ? poi.getNode() : null;
        if (node) {
          const floor = node.getFloor ? node.getFloor() : null;
          if (floor) {
            return floor.getName ? floor.getName() : floor.name;
          }
        }
      } catch (e) {
        // Silently fail
      }
      return null;
    },
    
    getPOIIcon(poi) {
      try {
        if (poi.getIconUrl) return poi.getIconUrl();
        if (poi.iconUrl) return poi.iconUrl;
      } catch (e) {
        // Silently fail
      }
      return null;
    },
    
    getPOIName(poi) {
      try {
        // Must pass language parameter to getName!
        if (typeof poi.getName === 'function') {
          const name = poi.getName(this.language || 'et');
          if (name && name !== false) {
            return name;
          }
        }
        // Fallback to names.translations
        if (poi.names && poi.names.translations) {
          const trans = poi.names.translations;
          return trans[this.language] || trans.et || trans.en || Object.values(trans)[0] || 'Unknown';
        }
      } catch (e) {
        // Silently fail
      }
      return 'Unknown';
    },
    
    showRoute() {
      if (!this.canShowRoute) return;
      
      const startName = this.getPOIName(this.startPOI);
      const endName = this.getPOIName(this.endPOI);
      console.log('Showing route from', startName, 'to', endName);
      
      try {
        const wf = this.$wayfinder;
        
        // Clear any existing path
        if (wf.clearPath) {
          wf.clearPath();
        }
        
        // Get nodes for pathfinding
        const startNode = this.startPOI.getNode ? this.startPOI.getNode() : null;
        const endNode = this.endPOI.getNode ? this.endPOI.getNode() : null;
        
        console.log('Start node:', startNode);
        console.log('Start node ID:', startNode ? startNode.id : 'none');
        console.log('End node:', endNode);
        console.log('End node ID:', endNode ? endNode.id : 'none');
        
        if (startNode && endNode) {
          // Set accessibility option if needed
          if (wf.options) {
            wf.options.avoidStairs = this.avoidStairs;
            wf.options.accessibleRoute = this.avoidStairs;
          }
          
          // SOLUTION: Temporarily change the kiosk to our start node
          const originalKiosk = wf.options.kiosk;
          console.log('Original kiosk:', originalKiosk);
          
          // Set our start node as the "kiosk" (source)
          wf.options.kiosk = startNode.id;
          console.log('Temporarily set kiosk to:', wf.options.kiosk);
          
          // Now call showPath - it will use our start node as source
          wf.showPath(endNode, this.endPOI);
          
          // Restore original kiosk after a delay (path is already calculated)
          setTimeout(() => {
            wf.options.kiosk = originalKiosk;
            console.log('Restored kiosk to:', originalKiosk);
          }, 500);
          
          // Highlight both POIs
          if (wf.clearHighlights) {
            wf.clearHighlights();
          }
          if (wf.setHighlights) {
            wf.setHighlights([this.startPOI, this.endPOI]);
          }
          
          // Show the floor of the start point
          if (wf.showFloor && startNode.getFloor) {
            wf.showFloor(startNode.getFloor());
          }
          
          this.hasActiveRoute = true;
          
          // Emit event for parent component
          this.$emit('routeShown', {
            start: this.startPOI,
            end: this.endPOI
          });
          
          console.log('🗺️ Route shown successfully!');
          
          // Auto-collapse panel on mobile/portrait to show more map
          if (this.shouldAutoCollapse) {
            setTimeout(() => {
              this.isExpanded = false;
            }, 300);
          }
        } else {
          console.error('Could not get nodes for POIs');
          console.error('startNode:', startNode, 'endNode:', endNode);
        }
      } catch (error) {
        console.error('Error showing route:', error);
      }
    },
    
    clearRoute() {
      try {
        const wf = this.$wayfinder;
        
        if (wf.clearPath) {
          wf.clearPath();
        }
        if (wf.clearHighlights) {
          wf.clearHighlights();
        }
        
        this.hasActiveRoute = false;
        this.$emit('routeCleared');
      } catch (error) {
        console.error('Error clearing route:', error);
      }
    },
    
    resetPanel() {
      this.clearRoute();
      this.startPOI = null;
      this.endPOI = null;
      this.startSearch = '';
      this.endSearch = '';
      this.searchResults = [];
      this.activeInput = null;
    }
  }
};
</script>

<style lang="postcss">
.directions-panel {
  position: absolute;
  top: 2.4rem;
  left: 2.4rem;
  z-index: 10;
  font-size: 1.6rem;
}

/* Collapsed toggle button */
.directions-toggle {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.6rem 2.4rem;
  background-color: var(--th-base, #fff);
  border-radius: 3rem;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
}

.directions-toggle:hover {
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.2);
}

.directions-toggle:active {
  background-color: var(--th-active);
  color: var(--th-active-content);
}

.directions-toggle .w-24 {
  width: 2.8rem !important;
  height: 2.8rem !important;
}

.toggle-label {
  font-weight: 600;
  font-size: 1.8rem;
}

/* Expanded panel */
.directions-content {
  width: 36rem;
  background-color: var(--th-base, #fff);
  border-radius: 1.6rem;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.directions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.6rem 2rem;
  border-bottom: 1px solid var(--th-border);
}

.header-title {
  font-weight: 700;
  font-size: 1.8rem;
}

.close-btn {
  width: 3.2rem;
  height: 3.2rem;
  border: none;
  background: var(--th-neutral2);
  border-radius: 50%;
  font-size: 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.close-btn:hover {
  background: var(--th-border);
}

/* Body */
.directions-body {
  padding: 2rem;
  position: relative;
}

/* Location inputs */
.location-input {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin-bottom: 1.2rem;
}

.input-icon {
  width: 2.4rem;
  height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-dot {
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 50%;
}

.start-dot {
  background-color: var(--th-active);
  border: 2px solid var(--th-active);
}

.end-dot {
  background-color: var(--th-accent);
  border: 2px solid var(--th-accent);
}

.input-wrapper {
  flex: 1;
  position: relative;
}

.location-field {
  width: 100%;
  padding: 1.2rem 3.6rem 1.2rem 1.6rem;
  border: 2px solid var(--th-border);
  border-radius: 1rem;
  font-size: 1.4rem;
  outline: none;
  transition: border-color 0.2s;
}

.location-field:focus {
  border-color: var(--th-active);
}

.location-field::placeholder {
  color: var(--th-neutral-content);
}

.clear-btn {
  position: absolute;
  right: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  width: 2.4rem;
  height: 2.4rem;
  border: none;
  background: var(--th-neutral2);
  border-radius: 50%;
  font-size: 1.6rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.clear-btn:hover {
  background: var(--th-border);
}

/* Swap button */
.swap-btn {
  position: absolute;
  right: 2rem;
  top: 4.8rem;
  width: 3.6rem;
  height: 3.6rem;
  border: 2px solid var(--th-active);
  background: var(--th-base);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--th-active);
  transition: all 0.2s;
}

.swap-btn:hover {
  background-color: var(--th-active);
  color: var(--th-base);
}

.swap-btn:active {
  transform: scale(0.95);
}

/* Search results dropdown */
.search-results {
  position: absolute;
  left: 2rem;
  right: 2rem;
  top: 12rem;
  max-height: 24rem;
  overflow-y: auto;
  background: var(--th-base);
  border: 1px solid var(--th-border);
  border-radius: 1rem;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 20;
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.2rem 1.6rem;
  cursor: pointer;
  transition: background-color 0.15s;
}

.search-result-item:hover {
  background-color: var(--th-neutral2);
}

.search-result-item:not(:last-child) {
  border-bottom: 1px solid var(--th-border);
}

.result-icon {
  width: 3.2rem;
  height: 3.2rem;
  object-fit: contain;
}

.result-info {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.result-name {
  font-weight: 500;
  font-size: 1.4rem;
}

.result-floor {
  font-size: 1.2rem;
  color: var(--th-neutral-content);
}

/* Options */
.directions-options {
  margin-top: 1.6rem;
  padding-top: 1.6rem;
  border-top: 1px solid var(--th-border);
}

.option-checkbox {
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  font-size: 1.4rem;
}

.option-checkbox input[type="checkbox"] {
  width: 1.8rem;
  height: 1.8rem;
  accent-color: var(--th-active);
}

.wheelchair-icon {
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
}

/* Footer */
.directions-footer {
  display: flex;
  gap: 1.2rem;
  padding: 2rem;
  border-top: 1px solid var(--th-border);
  background: var(--th-neutral2);
}

.show-route-btn {
  flex: 1;
}

.show-route-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.clear-route-btn {
  padding: 1.1rem 2rem;
}

/* Built-in Keyboard */
.directions-keyboard {
  padding: 1.2rem;
  background: var(--th-neutral2);
  border-top: 1px solid var(--th-border);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  align-items: center;
}

.keyboard-row {
  display: flex;
  gap: 0.3rem;
  justify-content: center;
}

.keyboard-key {
  min-width: 2.6rem;
  height: 3.4rem;
  background: var(--th-active-content, #333);
  color: white;
  border: none;
  border-radius: 0.6rem;
  font-size: 1.2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.1s;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  text-transform: uppercase;
  user-select: none;
}

.keyboard-key:active {
  background: var(--th-active);
  color: var(--th-active-content);
  transform: scale(0.95);
}

.keyboard-key.key-delete {
  min-width: 4rem;
  background: var(--th-negative, #850031);
  font-size: 1rem;
}

.keyboard-key.key-space {
  min-width: 12rem;
  background: var(--th-active-content, #333);
}

/* Active input highlight */
.location-field.input-active {
  border-color: var(--th-active);
  box-shadow: 0 0 0 2px rgba(var(--th-active-rgb, 255, 204, 0), 0.3);
}

/* Mobile responsive */
@media (max-width: 480px) and (orientation: portrait) {
  .directions-panel {
    top: 1.2rem;
    left: 1.2rem;
    right: 1.2rem;
  }
  
  .directions-content {
    width: 100%;
  }
  
  .directions-toggle {
    padding: 1.4rem 2rem;
  }
  
  .directions-toggle .w-24 {
    width: 2.4rem !important;
    height: 2.4rem !important;
  }
  
  .toggle-label {
    font-size: 1.6rem;
  }
  
  .header-title {
    font-size: 1.6rem;
  }
  
  .directions-body {
    padding: 1.6rem;
  }
  
  .location-field {
    padding: 1rem 3rem 1rem 1.2rem;
    font-size: 1.3rem;
  }
  
  .search-results {
    top: 10rem;
    max-height: 20rem;
  }
  
  .directions-footer {
    padding: 1.6rem;
  }
}

/* Portrait tablet - bigger panel */
@media (orientation: portrait) and (min-width: 641px) {
  .directions-panel {
    top: 3rem;
    left: 3rem;
    font-size: 1.8rem;
  }
  
  .directions-content {
    width: 48rem;
  }
  
  .directions-toggle {
    padding: 2rem 3rem;
  }
  
  .directions-toggle .w-24 {
    width: 3.2rem !important;
    height: 3.2rem !important;
  }
  
  .toggle-label {
    font-size: 2.2rem;
  }
  
  .directions-header {
    padding: 2rem 2.4rem;
  }
  
  .header-title {
    font-size: 2.2rem;
  }
  
  .close-btn {
    width: 4rem;
    height: 4rem;
    font-size: 2.4rem;
  }
  
  .directions-body {
    padding: 2.4rem;
  }
  
  .location-input {
    margin-bottom: 1.6rem;
  }
  
  .icon-dot {
    width: 1.6rem;
    height: 1.6rem;
  }
  
  .location-field {
    padding: 1.4rem 4rem 1.4rem 1.8rem;
    font-size: 1.6rem;
    border-radius: 1.2rem;
  }
  
  .swap-btn {
    width: 4.4rem;
    height: 4.4rem;
    top: 5.8rem;
  }
  
  .swap-btn svg {
    width: 24px;
    height: 24px;
  }
  
  .clear-btn {
    width: 3rem;
    height: 3rem;
    font-size: 1.8rem;
  }
  
  .search-results {
    top: 14rem;
    max-height: 28rem;
  }
  
  .search-result-item {
    padding: 1.4rem 1.8rem;
  }
  
  .result-icon {
    width: 4rem;
    height: 4rem;
  }
  
  .result-name {
    font-size: 1.6rem;
  }
  
  .result-floor {
    font-size: 1.4rem;
  }
  
  .directions-options {
    margin-top: 2rem;
    padding-top: 2rem;
  }
  
  .option-checkbox {
    font-size: 1.6rem;
  }
  
  .option-checkbox input[type="checkbox"] {
    width: 2.2rem;
    height: 2.2rem;
  }
  
  .wheelchair-icon {
    width: 2.4rem;
    height: 2.4rem;
  }
  
  .directions-footer {
    padding: 2.4rem;
  }
  
  .show-route-btn,
  .clear-route-btn {
    padding: 1.4rem 2.4rem;
    font-size: 1.6rem;
  }
  
  /* Keyboard for portrait tablet */
  .directions-keyboard {
    padding: 1.6rem;
    gap: 0.5rem;
  }
  
  .keyboard-row {
    gap: 0.4rem;
  }
  
  .keyboard-key {
    min-width: 3.2rem;
    height: 4rem;
    font-size: 1.4rem;
    border-radius: 0.8rem;
  }
  
  .keyboard-key.key-delete {
    min-width: 5rem;
    font-size: 1.2rem;
  }
  
  .keyboard-key.key-space {
    min-width: 16rem;
  }
}

/* Landscape - bigger panel */
@media (orientation: landscape) {
  .directions-panel {
    top: 2.4rem;
    left: 2.4rem;
    font-size: 1.8rem;
  }
  
  .directions-content {
    width: 44rem;
  }
  
  .directions-toggle {
    padding: 1.8rem 2.6rem;
  }
  
  .directions-toggle .w-24 {
    width: 3rem !important;
    height: 3rem !important;
  }
  
  .toggle-label {
    font-size: 2rem;
  }
  
  .directions-header {
    padding: 1.8rem 2.2rem;
  }
  
  .header-title {
    font-size: 2rem;
  }
  
  .close-btn {
    width: 3.6rem;
    height: 3.6rem;
    font-size: 2.2rem;
  }
  
  .directions-body {
    padding: 2.2rem;
  }
  
  .location-field {
    padding: 1.3rem 3.8rem 1.3rem 1.6rem;
    font-size: 1.5rem;
  }
  
  .swap-btn {
    width: 4rem;
    height: 4rem;
    top: 5.2rem;
  }
  
  .swap-btn svg {
    width: 22px;
    height: 22px;
  }
  
  .search-results {
    top: 13rem;
  }
  
  .result-name {
    font-size: 1.5rem;
  }
  
  .option-checkbox {
    font-size: 1.5rem;
  }
  
  .directions-footer {
    padding: 2rem;
  }
  
  .show-route-btn,
  .clear-route-btn {
    padding: 1.2rem 2rem;
    font-size: 1.5rem;
  }
  
  /* Keyboard for landscape */
  .directions-keyboard {
    padding: 1.4rem;
    gap: 0.4rem;
  }
  
  .keyboard-row {
    gap: 0.35rem;
  }
  
  .keyboard-key {
    min-width: 3rem;
    height: 3.6rem;
    font-size: 1.3rem;
  }
  
  .keyboard-key.key-delete {
    min-width: 4.5rem;
    font-size: 1.1rem;
  }
  
  .keyboard-key.key-space {
    min-width: 14rem;
  }
}
</style>